import React from 'react';
import { Document, Page, View, StyleSheet } from '@react-pdf/renderer';
import { PDF_THEMES, DEFAULT_THEME } from '../themes';
import PdfHeader from '../components/PdfHeader';
import PdfFooter from '../components/PdfFooter';
import PdfSummaryCards from '../components/PdfSummaryCards';
import { PdfTableHead, PdfTableRow, PdfTableFoot } from '../components/PdfTable';
import { fmtINR, fmtDate, cap } from '../pdfUtils';

const s = StyleSheet.create({
  page:    { fontFamily: 'NotoSans', paddingBottom: 60 },
  content: { marginHorizontal: 40, marginTop: 12 },
});

const buildCols = (cats) => [
  { label: 'Date',       flex: 0.8 },
  { label: 'Receipt No', flex: 0.9 },
  { label: 'Member',     flex: 1.4 },
  ...cats.map((c) => ({ label: c.name, flex: 1.0, align: 'right' })),
  { label: 'Mode',       flex: 0.9 },
  { label: 'Total (Rs)', flex: 1.0, align: 'right' },
];

export default function FullInfoDocument({ data, theme: themeKey, orientation, sections }) {
  const t = PDF_THEMES[themeKey] || PDF_THEMES[DEFAULT_THEME];
  const rows = Array.isArray(data.rows) ? data.rows
    : Array.isArray(data.receipts) ? data.receipts : [];
  const cats = Array.isArray(data.categories) ? data.categories : [];
  const total = data.totalAmount ?? rows.reduce((s, r) => s + Number(r.amountInr || 0), 0);

  const COLS = buildCols(cats);

  const catTotals = cats.map((c) =>
    rows.reduce((s, r) => s + (r.catAmounts?.[String(c.id)] || 0), 0)
  );

  return (
    <Document>
      <Page size="A4" orientation={orientation || 'landscape'} style={[s.page, { backgroundColor: t.white }]}>
        {sections.header && <PdfHeader derasar={data.derasar} reportTitle="Receipts Book" theme={t} />}
        <View style={s.content}>
          {sections.summaryCards && (
            <PdfSummaryCards theme={t} items={[
              { label: 'Total Receipts', value: String(rows.length), color: t.primary },
              { label: 'Total Amount',   value: fmtINR(total),       color: t.body   },
            ]} />
          )}
          <PdfTableHead cols={COLS} theme={t} />
          {rows.map((row, i) => (
            <PdfTableRow key={i} cols={COLS} index={i} theme={t}
              values={[
                fmtDate(row.receiptDate || row.date),
                row.receiptNo || '—',
                row.memberName || row.donorName || '—',
                ...cats.map((c) => {
                  const amt = row.catAmounts?.[String(c.id)] || 0;
                  return amt > 0 ? fmtINR(amt) : '—';
                }),
                cap(row.paymentMode),
                fmtINR(row.amountInr),
              ]}
            />
          ))}
          <PdfTableFoot
            cols={COLS}
            values={[
              '', `${rows.length} receipts`, '',
              ...catTotals.map((t) => t > 0 ? fmtINR(t) : '—'),
              '',
              fmtINR(total),
            ]}
            theme={t}
          />
        </View>
        {sections.footer && <PdfFooter theme={t} />}
      </Page>
    </Document>
  );
}
